import { Takeover, Sponsorstripe } from '@schibsted/advertory/aftenposten/cjs';
import React, { useEffect } from 'react';

import type { ByfunnFront } from '../../../types/frapi-types.js';
import type { RenderContext } from '../../../../aftenposten/views/layouts/rendering/types.js';

import {
  useByfunnPulseContext,
  buildFrontpageView,
  withByfunnPulse,
} from '../../../pulse/index.js';
import BundleDesktop from '../../components/bundles/BundleDesktop.js';
import BundleMobile from '../../components/bundles/BundleMobile.js';
import Footer from '../../components/commonComponents/Footer.js';
import Navigation from '../../components/commonComponents/Navigation/Navigation.js';
import { DataController } from '../../components/commonComponents/DataController.js';
import Teaser from '../../components/teasers/Teaser.js';
import { splitToChunks } from './frontHelpers.js';
import { renderWidget } from './RenderWidget.js';
import { desktopWidgetConfig, mobileWidgetConfig } from './widgetConfig.js';
import Ad from '../../components/ads/Ad/Ad.js';
import { AdContexts } from '../../../types/ad-types.js';
import { insertAdsPlacements } from '../../components/ads/Ad/helpers.js';
import { useSessionData } from '../../../../../public-src/core/js/schibsted-account/react.server.js';
import { ListsProvider } from '../../components/saving/ListsProvider.js';
import useInitializeAds from '../../hooks/useInitAds.js';
import { shouldCompressBundle } from '../../components/bundles/utils.js';
import PWAPopup from '../../components/commonComponents/PWAPopup.js';
import { usePWAInstallPrompt } from '../../hooks/usePWAInstallPrompt.js';
import { useAdjustAdvertoryTakeoverStyles } from '../../hooks/useAdjustAdvertoryTakeoverStyles.js';
import usePersonalization from '../../hooks/usePersonalization.js';

type FrontpageProps = {
  response: ByfunnFront;
  renderContext: RenderContext;
  isLoggedIn: boolean;
};

const Frontpage: React.FC<FrontpageProps> = ({
  response,
  renderContext,
  isLoggedIn,
}) => {
  const { siteVersion, isHermesApp } = renderContext;
  const isMobile = siteVersion === 'phone';

  const session = useSessionData();
  const { registerViewElementTracking, trackViewFrontpage } =
    useByfunnPulseContext();

  const {
    shouldShowPrompt,
    incrementVisitCounter,
    incrementPWACloseCounter,
    setPWADate,
    setPWAInstalled,
  } = usePWAInstallPrompt();

  useEffect(() => {
    incrementVisitCounter();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useAdjustAdvertoryTakeoverStyles(true);

  const handleClosePWAOverlay = async () => {
    setPWADate();
    incrementPWACloseCounter();
  };

  const personalization = usePersonalization(response);

  useEffect(() => {
    const { legacy, tpaas } = buildFrontpageView(personalization);

    trackViewFrontpage(legacy, tpaas);
    registerViewElementTracking();
  }, [trackViewFrontpage, personalization, registerViewElementTracking]);

  useInitializeAds(session, siteVersion);

  const chunks = splitToChunks(response.feed.items);

  // insert widgets
  const widgets = isMobile ? mobileWidgetConfig : desktopWidgetConfig;
  widgets.map((w) => {
    chunks.splice(w.position, 0, { type: 'widget', component: w.widget });
  });

  const chunksWithAds = insertAdsPlacements(
    isMobile,
    chunks,
    AdContexts.FRONTPAGE,
  );

  let mobileRows = 1;

  return (
    <ListsProvider>
      <Takeover targetId="takeover" />
      <div className={`${siteVersion}-front advertory-container-anchor`}>
        <Navigation fullWidth={!isMobile} isHermesApp={isHermesApp} />

        <Sponsorstripe targetId="sponsorstripe" />

        {shouldShowPrompt && (
          <PWAPopup
            handleClose={handleClosePWAOverlay}
            setPWAInstalled={setPWAInstalled}
            isIOS
          />
        )}

        {chunksWithAds.map((c, i): React.ReactNode => {
          const index = isMobile && i > 0 ? mobileRows : i + 1;

          switch (c.type) {
            case 'teaser':
              if (c.fullWidthTeaser) {
                mobileRows++;

                return (
                  <Teaser
                    key={c.id}
                    {...c}
                    index={index}
                    pulseData={{
                      contentSource: 'Editorial',
                      placement: {
                        curateContext: personalization.context,
                      },
                    }}
                  />
                );
              }

              return <></>;
            case 'widget':
              mobileRows++;

              return renderWidget(c, isMobile, index, response, isLoggedIn);
            case 'ad':
              mobileRows++;

              return (
                <Ad
                  key={`Ad-${c.id}`}
                  id={c.id}
                  cssClass={c.cssClass}
                  isMobile={isMobile}
                />
              );
            case 'chunk':
            case 'bundle':
              mobileRows += shouldCompressBundle(c, isMobile)
                ? 2
                : c.items.length;

              return isMobile ? (
                <BundleMobile
                  {...c}
                  key={c.id}
                  index={index}
                  curateContext={personalization.context}
                />
              ) : (
                <BundleDesktop
                  {...c}
                  key={c.id}
                  index={index}
                  curateContext={personalization.context}
                />
              );
            default:
              return <></>;
          }
        })}
        <Footer isMobile={isMobile} />
        <DataController />
      </div>
    </ListsProvider>
  );
};

const FrontpageWithPulse = withByfunnPulse(Frontpage);

export { FrontpageWithPulse as Frontpage };
